interface NetworkInformationLike {
  saveData?: boolean;
  effectiveType?: string;
}

interface IdleScheduler {
  requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
  cancelIdleCallback?: (id: number) => void;
}

interface IdleRoutePreloaderOptions {
  window?: IdleScheduler;
  delayMs?: number;
  idleTimeoutMs?: number;
}

export const highFrequencyPreloadPaths = [
  '/invoices',
  '/invoices/issue',
  '/customers',
  '/numbering',
  '/exceptions',
  '/iam',
] as const;

export function shouldPreloadRoutes(connection?: NetworkInformationLike) {
  return connection?.saveData !== true
    && connection?.effectiveType !== 'slow-2g'
    && connection?.effectiveType !== '2g';
}

export function getIdlePreloadPaths(availablePaths: readonly string[], currentPath: string) {
  const available = new Set(availablePaths);
  return highFrequencyPreloadPaths.filter((path) => path !== currentPath && available.has(path));
}

export function scheduleIdleRoutePreloads(
  paths: readonly string[],
  preload: (path: string) => Promise<unknown>,
  options: IdleRoutePreloaderOptions = {},
) {
  const idleWindow: IdleScheduler = options.window ?? window;
  const delayMs = options.delayMs ?? 800;
  const idleTimeoutMs = options.idleTimeoutMs ?? 2_000;
  let cancelled = false;
  let idleId: number | undefined;
  let timerId: ReturnType<typeof globalThis.setTimeout> | undefined;
  let preloadIndex = 0;

  const scheduleNext = () => {
    if (cancelled || preloadIndex >= paths.length) return;
    const preloadNext = () => {
      if (cancelled) return;
      const path = paths[preloadIndex++];
      if (!path) return;
      void preload(path).finally(scheduleNext);
    };

    if (idleWindow.requestIdleCallback) {
      idleId = idleWindow.requestIdleCallback(preloadNext, { timeout: idleTimeoutMs });
    } else {
      timerId = globalThis.setTimeout(preloadNext, delayMs);
    }
  };

  scheduleNext();

  return () => {
    cancelled = true;
    if (idleId !== undefined) idleWindow.cancelIdleCallback?.(idleId);
    if (timerId !== undefined) globalThis.clearTimeout(timerId);
  };
}
